import BaseStore from './BaseStore';
import { ActionTypes } from '../Constants';
import { type Project, type Scene, type Character, type Location, type Plot } from '../../types/entities';

class ProjectStore extends BaseStore {
   private projects: Project[] = [];
   private selectedProjectId: string | null = null;
   private scenes: Scene[] = [];
   private selectedSceneId: string | null = null;
   private characters: Character[] = [];
   private selectedCharacterId: string | null = null;
   private locations: Location[] = [];
   private selectedLocationId: string | null = null;
   private plots: Plot[] = [];
   private selectedPlotId: string | null = null;
   private isLoading = false;
   private error: string | null = null;
   private projectsInitialized = false;

   private addIfMissing<T extends { id?: string }>(collection: T[], item: T, addToFront = false): T[] {
      if (!item.id) return collection;
      if (collection.some((entry) => entry.id === item.id)) {
         return collection;
      }
      return addToFront ? [item, ...collection] : [...collection, item];
   }

   constructor() {
      super();

      this.register((action) => {
         switch (action.type) {
            // Project Actions
            case ActionTypes.PROJECTS_LOADED:
               this.projects = (action.projects as Project[]) || [];
               this.isLoading = false;
               this.projectsInitialized = true;
               this.emitChange();
               break;

            case ActionTypes.CREATE_PROJECT:
               {
                  const project = (action.project as Project) || {};
                  this.projects = this.addIfMissing(this.projects, project, true);
                  this.selectedProjectId = project.id ?? null;
               }
               this.emitChange();
               break;

            case ActionTypes.UPDATE_PROJECT:
               this.projects = this.projects.map((p) => {
                  if (p.id === action.projectId) {
                     const updates = (action.updates as Partial<Project>) || {};
                     return { ...p, ...updates };
                  }
                  return p;
               });
               this.emitChange();
               break;

            case ActionTypes.DELETE_PROJECT:
               this.projects = this.projects.filter((p) => p.id !== action.projectId);
               if (this.selectedProjectId === action.projectId) {
                  this.selectedProjectId = null;
                  this.scenes = [];
                  this.characters = [];
                  this.locations = [];
                  this.plots = [];
               }
               this.emitChange();
               break;

            case ActionTypes.SELECT_PROJECT:
               this.selectedProjectId = (action.projectId as string) ?? null;
               // Clear selections when changing projects
               this.selectedSceneId = null;
               this.selectedCharacterId = null;
               this.selectedLocationId = null;
               this.selectedPlotId = null;
               this.emitChange();
               break;

               // Scene Actions
            case ActionTypes.SCENES_LOADED:
               this.scenes = (action.scenes as Scene[]) || [];
               this.emitChange();
               break;

            case ActionTypes.CREATE_SCENE:
               {
                  const scene = (action.scene as Scene) || {};
                  this.scenes = this.addIfMissing(this.scenes, scene);
                  this.selectedSceneId = scene.id ?? null;
               }
               this.emitChange();
               break;

            case ActionTypes.UPDATE_SCENE:
               this.scenes = this.scenes.map((s) => {
                  if (s.id === action.sceneId) {
                     const updates = (action.updates as Partial<Scene>) || {};
                     return { ...s, ...updates };
                  }
                  return s;
               });
               this.emitChange();
               break;

            case ActionTypes.DELETE_SCENE:
               this.scenes = this.scenes.filter((s) => s.id !== action.sceneId);
               if (this.selectedSceneId === action.sceneId) {
                  this.selectedSceneId = null;
               }
               this.emitChange();
               break;

            case ActionTypes.SELECT_SCENE:
               this.selectedSceneId = (action.sceneId as string) ?? null;
               this.emitChange();
               break;

               // Character Actions
            case ActionTypes.CHARACTERS_LOADED:
               this.characters = (action.characters as Character[]) || [];
               this.emitChange();
               break;

            case ActionTypes.CREATE_CHARACTER:
               {
                  const character = (action.character as Character) || {};
                  this.characters = this.addIfMissing(this.characters, character);
                  this.selectedCharacterId = character.id ?? null;
               }
               this.emitChange();
               break;

            case ActionTypes.UPDATE_CHARACTER:
               this.characters = this.characters.map((c) => {
                  if (c.id === action.characterId) {
                     const updates = (action.updates as Partial<Character>) || {};
                     return { ...c, ...updates };
                  }
                  return c;
               });
               this.emitChange();
               break;

            case ActionTypes.DELETE_CHARACTER:
               this.characters = this.characters.filter((c) => c.id !== action.characterId);
               if (this.selectedCharacterId === action.characterId) {
                  this.selectedCharacterId = null;
               }
               this.emitChange();
               break;

            case ActionTypes.SELECT_CHARACTER:
               this.selectedCharacterId = (action.characterId as string) ?? null;
               this.emitChange();
               break;

               // Location Actions
            case ActionTypes.LOCATIONS_LOADED:
               this.locations = (action.locations as Location[]) || [];
               this.emitChange();
               break;

            case ActionTypes.CREATE_LOCATION:
               {
                  const location = (action.location as Location) || {};
                  this.locations = this.addIfMissing(this.locations, location);
                  this.selectedLocationId = location.id ?? null;
               }
               this.emitChange();
               break;

            case ActionTypes.UPDATE_LOCATION:
               this.locations = this.locations.map((l) => {
                  if (l.id === action.locationId) {
                     const updates = (action.updates as Partial<Location>) || {};
                     return { ...l, ...updates };
                  }
                  return l;
               });
               this.emitChange();
               break;

            case ActionTypes.DELETE_LOCATION:
               this.locations = this.locations.filter((l) => l.id !== action.locationId);
               if (this.selectedLocationId === action.locationId) {
                  this.selectedLocationId = null;
               }
               this.emitChange();
               break;

            case ActionTypes.SELECT_LOCATION:
               this.selectedLocationId = (action.locationId as string) ?? null;
               this.emitChange();
               break;

               // Plot Actions
            case ActionTypes.PLOTS_LOADED:
               this.plots = (action.plots as Plot[]) || [];
               this.emitChange();
               break;

            case ActionTypes.CREATE_PLOT:
               {
                  const plot = (action.plot as Plot) || {};
                  this.plots = this.addIfMissing(this.plots, plot);
                  this.selectedPlotId = plot.id ?? null;
               }
               this.emitChange();
               break;

            case ActionTypes.UPDATE_PLOT:
               this.plots = this.plots.map((p) => {
                  if (p.id === action.plotId) {
                     const updates = (action.updates as Partial<Plot>) || {};
                     return { ...p, ...updates };
                  }
                  return p;
               });
               this.emitChange();
               break;

            case ActionTypes.DELETE_PLOT:
               this.plots = this.plots.filter((p) => p.id !== action.plotId);
               if (this.selectedPlotId === action.plotId) {
                  this.selectedPlotId = null;
               }
               this.emitChange();
               break;

            case ActionTypes.SELECT_PLOT:
               this.selectedPlotId = (action.plotId as string) ?? null;
               this.emitChange();
               break;

               // Loading/Error Actions
            case ActionTypes.SET_LOADING:
               this.isLoading = Boolean(action.isLoading);
               this.emitChange();
               break;

            case ActionTypes.SET_ERROR:
               this.error = (action.error as string) || null;
               this.emitChange();
               break;

            case ActionTypes.CLEAR_ERROR:
               this.error = null;
               this.emitChange();
               break;

            case ActionTypes.LOGOUT:
               this.projects = [];
               this.selectedProjectId = null;
               this.scenes = [];
               this.selectedSceneId = null;
               this.characters = [];
               this.selectedCharacterId = null;
               this.locations = [];
               this.selectedLocationId = null;
               this.plots = [];
               this.selectedPlotId = null;
               this.isLoading = false;
               this.error = null;
               this.projectsInitialized = false;
               this.emitChange();
               break;

            default:
          // Do nothing
         }
      });
   }

   // Project getters
   getProjects(): Project[] {
      return this.projects;
   }

   getSelectedProject(): Project | undefined {
      return this.projects.find((p) => p.id === this.selectedProjectId);
   }

   getSelectedProjectId(): string | null {
      return this.selectedProjectId;
   }

   // Scene getters
   getScenes(): Scene[] {
      return this.scenes;
   }

   getSelectedScene(): Scene | undefined {
      return this.scenes.find((s) => s.id === this.selectedSceneId);
   }

   getSelectedSceneId(): string | null {
      return this.selectedSceneId;
   }

   // Character getters
   getCharacters(): Character[] {
      return this.characters;
   }

   getSelectedCharacter(): Character | undefined {
      return this.characters.find((c) => c.id === this.selectedCharacterId);
   }

   getSelectedCharacterId(): string | null {
      return this.selectedCharacterId;
   }

   // Location getters
   getLocations(): Location[] {
      return this.locations;
   }

   getSelectedLocation(): Location | undefined {
      return this.locations.find((l) => l.id === this.selectedLocationId);
   }

   getSelectedLocationId(): string | null {
      return this.selectedLocationId;
   }

   // Plot getters
   getPlots(): Plot[] {
      return this.plots;
   }

   getSelectedPlot(): Plot | undefined {
      return this.plots.find((p) => p.id === this.selectedPlotId);
   }

   getSelectedPlotId(): string | null {
      return this.selectedPlotId;
   }

   // Loading/Error getters
   isProjectsInitialized(): boolean {
      return this.projectsInitialized;
   }

   getIsLoading(): boolean {
      return this.isLoading;
   }

   getError(): string | null {
      return this.error;
   }
}

const projectStore = new ProjectStore();

export default projectStore;
